import { ROUTES } from '@sportninja/common/constants/app'

/**
 * SN-8340 — where an in-app Team-Managed Budget money notice takes the player.
 *
 * Two notices carry a payment link: the allocation notice ("your team payment schedule is
 * ready") and its reminder twin. Neither was navigable — `notificationTypes` had no entry
 * for either, so NotificationItem's switch fell through to '' and clicking did nothing.
 *
 * The two store DIFFERENT shapes, which is the whole reason this is a function and not a
 * field read. The allocation notice now emits a RELATIVE path; the reminder has always
 * stored an ABSOLUTE one (TeamBalanceCommunicationService builds it from the app URL), and
 * rows written before this change still do. A reader that accepted only relative paths
 * would silently drop every existing reminder link.
 */

/** "My Registrations" — the player's own list, and where every payable invoice is reachable. */
export const BALANCE_NOTIFICATION_FALLBACK: string = ROUTES.REGISTRATIONS.ROOT

const PAY_PATH = '/pay'

/**
 * The stored link, narrowed to THIS app's pay route — or null.
 *
 * Deliberately narrow. `history.push` accepts whatever it is given, so an absolute URL
 * taken from stored data is an open redirect waiting to happen: only the path and query
 * of a URL whose path is exactly `/pay` survive, and the host is discarded rather than
 * trusted. Anything else — another path, another scheme, junk — returns null and the
 * caller falls back.
 */
export const toPayRoute = (raw: unknown): string | null => {
  if (typeof raw !== 'string' || raw.length === 0) {
    return null
  }

  if (raw === PAY_PATH || raw.startsWith(`${PAY_PATH}?`)) {
    return raw
  }

  try {
    const parsed = new URL(raw)
    if (parsed.pathname === PAY_PATH) {
      return `${parsed.pathname}${parsed.search}`
    }
  } catch {
    // Not an absolute URL — nothing to normalise.
  }

  return null
}

/**
 * Never returns '' — an actionable money notice must always go somewhere. A notice with
 * no payable bill (offline, settled, charge in flight) and an OLD row that predates
 * `action_url` both land on the registrations list, which is a true statement either way.
 */
export const resolveBalanceNotificationUrl = (actionUrl: unknown): string =>
  toPayRoute(actionUrl) ?? BALANCE_NOTIFICATION_FALLBACK
